import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, computed, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { InfoModalService } from '../../../core/services/info-modal.service';

export interface InfoModalSection {
  label: string;
  body: string[];
}

@Component({
  selector: 'app-info-modal',
  imports: [CommonModule, MatIconModule],
  templateUrl: './info-modal.html',
  styleUrls: ['./info-modal.scss'],
})
export class InfoModalComponent implements OnInit, OnDestroy {
  private readonly infoModal = inject(InfoModalService);
  private readonly el = inject(ElementRef);

  readonly visible = this.infoModal.visible;
  readonly title = this.infoModal.title;
  readonly message = this.infoModal.message;

  /**
   * Splits the message into "Label\nBody text" blocks (blank-line separated) so
   * multi-field guidance renders as a labeled list instead of one dense paragraph.
   * Messages without that shape fall back to a single unlabeled block.
   */
  readonly sections = computed<InfoModalSection[]>(() => {
    const blocks = this.message().split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
    return blocks.map((block) => {
      const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
      if (lines.length > 1) {
        return { label: lines[0], body: lines.slice(1) };
      }
      return { label: '', body: lines };
    });
  });

  private hostEl!: HTMLElement;

  ngOnInit(): void {
    this.hostEl = this.el.nativeElement as HTMLElement;

    const modalRoot = document.getElementById('modal-root');
    if (modalRoot) {
      modalRoot.appendChild(this.hostEl);
    } else {
      document.body.appendChild(this.hostEl);
    }
  }

  ngOnDestroy(): void {
    this.hostEl.remove();
  }

  close(): void {
    this.infoModal.close();
  }

  onOverlayClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }
}
